/**
 * AttachmentViewer — in-app overlay for post/DM file attachments.
 * Opens files OVER the app (no navigation, no new window) so desktop users
 * never lose their draft: PDFs/images/text render inline, everything else
 * gets a download prompt. Esc, X, or backdrop click closes.
 */
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Download, FileText, Loader2 } from 'lucide-react';

// Only http(s) attachment links are ever rendered or navigated to: a stored
// "javascript:" / "data:" URL in an <iframe src> or <a href> would run in the
// app's origin (final review 2026-09-07, comms P1-1).
export const isHttpUrl = (u) => /^https?:\/\//i.test(u || '');
const isPdf = (f) => (f.type || '').includes('pdf') || /\.pdf($|\?)/i.test(f.filename || f.url || '');
const isImage = (f) => (f.type || '').startsWith('image/');
const isText = (f) => (f.type || '').startsWith('text/');

export const AttachmentViewer = ({ file, onClose }) => {
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    window.addEventListener('keydown', onKey, true);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey, true);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  const handleDownload = async () => {
    if (!isHttpUrl(file?.url)) return;
    setDownloading(true);
    try {
      const res = await fetch(file.url);
      if (!res.ok) throw new Error('fetch failed');
      const blob = await res.blob();
      const href = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href;
      a.download = file.filename || 'attachment';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(href), 4000);
    } catch (err) {
      const a = document.createElement('a');
      a.href = file.url;
      a.download = file.filename || 'attachment';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } finally {
      setDownloading(false);
    }
  };

  if (!file) return null;
  const safeUrl = isHttpUrl(file.url);
  const image = isImage(file) && safeUrl;
  const framePreview = (isPdf(file) || isText(file)) && safeUrl;

  return createPortal(
    <div
      className="fixed inset-0 z-[200] flex flex-col bg-black/80 backdrop-blur-sm"
      onClick={onClose}
      data-testid="attachment-viewer"
    >
      <div
        className="flex items-center gap-3 px-4 py-3 bg-[#1e1f22] border-b border-white/10 flex-shrink-0"
        onClick={(e) => e.stopPropagation()}
      >
        <FileText className="w-5 h-5 text-slate-400 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-white truncate">{file.filename || 'Attachment'}</p>
          {file.size ? (
            <p className="text-xs text-slate-400">{(file.size / 1024).toFixed(0)} KB</p>
          ) : null}
        </div>
        <button
          type="button"
          onClick={handleDownload}
          disabled={downloading || !safeUrl}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white text-sm font-medium transition-colors disabled:opacity-60"
          data-testid="attachment-viewer-download"
        >
          {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          <span className="hidden sm:inline">Download</span>
        </button>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close attachment"
          className="p-2 rounded-lg hover:bg-white/15 text-slate-300 hover:text-white transition-colors"
          data-testid="attachment-viewer-close"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
      <div className="flex-1 min-h-0 p-3 sm:p-5 flex items-center justify-center" onClick={onClose}>
        {image ? (
          <img
            src={file.url}
            alt={file.filename || 'attachment'}
            className="max-w-full max-h-full object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        ) : framePreview ? (
          <iframe
            src={file.url}
            title={file.filename || 'attachment'}
            className="w-full h-full rounded-lg bg-white border-0"
          />
        ) : (
          <div
            className="bg-[#1e1f22] rounded-xl p-8 text-center max-w-sm mx-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="attachment-viewer-no-preview"
          >
            <FileText className="w-10 h-10 text-slate-500 mx-auto mb-3" />
            <p className="text-sm font-medium text-white mb-1 break-words">{file.filename}</p>
            <p className="text-xs text-slate-400 mb-5">{safeUrl ? 'No preview available for this file type.' : "This attachment's link is not a web address and can't be opened."}</p>
            <button
              type="button"
              onClick={handleDownload}
              disabled={downloading || !safeUrl}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium transition-colors disabled:opacity-60"
            >
              {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Download file
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default AttachmentViewer;
